// ============================================================================
// EMPLOYEE ACTIVITY REPORT — DRILL-DOWN VERSION
// ============================================================================
// Uses SyutDrillScreen to enable multi-level drill-down:
//   Employee → ChargeType → Engagement → Activity → WorkDate → Entry
//
// The base filter panel (date range, employee, manager, engagement, charge type)
// persists across drill levels. The drill breadcrumb shows the path.

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { SyutDrillScreen } from 'components/ui/SyutDrill';
import type { FilterDisplayItem } from 'components/ui/SyutDrill';
import type { DrillDimension } from 'contexts/DrillContext';
import {
  SyutClientSelect,
  SyutEmployeeSelect,
  SyutScopedEmployeeSelect,
  SyutEngagementSelect,
} from 'components/ui/SyutEntitySelect';
import { useChargeTypes } from 'hooks/api/useChargeTypes';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function firstOfMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateRange(from?: string, to?: string): string {
  if (!from && !to) return '';
  const fmt = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(d).padStart(2, '0')} ${months[m - 1]} ${y}`;
  };
  if (from && to) return `${fmt(from)} \u2013 ${fmt(to)}`;
  if (from) return `From ${fmt(from)}`;
  return `Until ${fmt(to!)}`;
}

// ---------------------------------------------------------------------------
// Available drill dimensions and default path
// ---------------------------------------------------------------------------

const ALL_DIMENSIONS: DrillDimension[] = [
  'Employee', 'Manager', 'Engagement', 'Client',
  'EngagementType', 'Activity', 'ChargeType', 'WorkDate', 'Entry',
];

const DEFAULT_DRILL_PATH: DrillDimension[] = [
  'Employee', 'ChargeType', 'Engagement', 'Activity', 'WorkDate', 'Entry',
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface EmployeeActivityReportScreenProps {
  /** Module-action codes granted to this user. */
  allowedActions?: string[];
}

const EmployeeActivityReportScreen: React.FC<EmployeeActivityReportScreenProps> = ({ allowedActions = [] }) => {
  // Permissions check (used by backend via moduleContext below; the dropdown
  // limits are applied server-side too — frontend is informational only here).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _permissions = { canViewAll: allowedActions.includes('view-all-employees'), canViewTeam: allowedActions.includes('view-own-team') };
  // --- Base filter state (persists across drill levels) ---
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [employeeId, setEmployeeId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [engagementId, setEngagementId] = useState('');
  const [chargeTypeKey, setChargeTypeKey] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Lookups for charge type filter
  const { data: chargeTypesRaw } = useChargeTypes();
  const chargeTypeOptions = useMemo(() => [
    { value: '', label: 'All charge types' },
    ...(chargeTypesRaw || []).map((ct: any) => ({
      value: ct.key || ct.code || '',
      label: ct.name || ct.label || ct.value,
    })).filter((o: any) => o.value),
  ], [chargeTypesRaw]);

  // --- Base query params (sent to the drill endpoint alongside drill filters) ---
  const baseQueryParams = useMemo(() => {
    const p: Record<string, any> = {
      // Tells the backend which module's permissions to evaluate for visibility scope
      moduleContext: 'employee-activity-report',
    };
    if (fromDate) p.fromDate = fromDate;
    if (toDate) p.toDate = toDate;
    if (employeeId) p.employeeId = employeeId;
    if (managerId) p.managerId = managerId;
    if (engagementId) p.engagementId = engagementId;
    if (chargeTypeKey) p.chargeTypeKey = chargeTypeKey;
    return p;
  }, [fromDate, toDate, employeeId, managerId, engagementId, chargeTypeKey]);

  // --- Filter display items for the summary bar ---
  const baseFilterDisplay: FilterDisplayItem[] = useMemo(() => {
    const items: FilterDisplayItem[] = [];
    items.push({ label: 'Period', value: formatDateRange(fromDate, toDate) || 'All' });
    items.push({ label: 'Employee', value: employeeId ? 'Selected' : 'All' });
    items.push({ label: 'Manager', value: managerId ? 'Selected' : 'All' });
    items.push({ label: 'Engagement', value: engagementId ? 'Selected' : 'All' });
    const ct = chargeTypeOptions.find(o => o.value === chargeTypeKey);
    items.push({ label: 'Charge Type', value: ct?.label || 'All' });
    return items;
  }, [fromDate, toDate, employeeId, managerId, engagementId, chargeTypeKey, chargeTypeOptions]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (fromDate || toDate) c++;
    if (employeeId) c++;
    if (managerId) c++;
    if (engagementId) c++;
    if (chargeTypeKey) c++;
    return c;
  }, [fromDate, toDate, employeeId, managerId, engagementId, chargeTypeKey]);

  const handleResetFilters = useCallback(() => {
    setFromDate(firstOfMonth());
    setToDate(today());
    setEmployeeId('');
    setManagerId('');
    setEngagementId('');
    setChargeTypeKey('');
  }, []);

  // --- Filter panel JSX ---
  const filterPanel = filterOpen ? (
    <div className="search-panel">
      <div className="search-panel-header">
        <h3 className="search-panel-title">Filters</h3>
        <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close">
          <IconSystem.x size={14} />
        </button>
      </div>
      <div className="search-panel-content">
        <div className="filter-section">
          <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
          <input type="date" className="form-input" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
        </div>
        <div className="filter-section">
          <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
          <input type="date" className="form-input" value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
        </div>
        <div className="filter-section">
          <SyutScopedEmployeeSelect
            label="Employee"
            scopeMode="view"
            moduleId="employee-activity-report"
            value={employeeId || undefined}
            onChange={(val) => setEmployeeId(val)}
            isClearable
          />
        </div>
        <div className="filter-section">
          <SyutScopedEmployeeSelect
            label="Manager (reportees)"
            scopeMode="view"
            moduleId="employee-activity-report"
            role="manager"
            value={managerId || undefined}
            onChange={(val) => setManagerId(val)}
            isClearable
          />
        </div>
        <div className="filter-section">
          <SyutEngagementSelect
            label="Engagement"
            value={engagementId || undefined}
            onChange={(val) => setEngagementId(val)}
            isClearable
          />
        </div>
        <div className="filter-section">
          <SyutSelect
            label="Charge Type"
            options={chargeTypeOptions}
            value={chargeTypeKey}
            onChange={(val) => setChargeTypeKey((val as string) || '')}
            isClearable={false}
            size="sm"
          />
        </div>
      </div>
      <div className="filter-actions">
        <button className="btn btn-secondary" onClick={handleResetFilters}>Clear</button>
        <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
      </div>
    </div>
  ) : null;

  return (
    <SyutDrillScreen
      title="Employee Activity Report"
      description="Drill-down timesheet aggregation by employee, engagement, activity, charge type, and more"
      icon={<IconSystem.chart size={18} />}
      rootGroupBy="Employee"
      defaultDrillPath={DEFAULT_DRILL_PATH}
      availableDimensions={ALL_DIMENSIONS}
      baseQueryParams={baseQueryParams}
      baseFilterDisplay={baseFilterDisplay}
      onResetBaseFilters={handleResetFilters}
      filterPanel={filterPanel}
      filterOpen={filterOpen}
      onToggleFilter={() => setFilterOpen(prev => !prev)}
      activeFilterCount={activeFilterCount}
      searchPlaceholder="Search employee, engagement, activity..."
    />
  );
};

export default EmployeeActivityReportScreen;
